import { useState } from "react";
import {
  ExperimentVariation,
  HackleExperimentContainer,
} from "@/components/HackleExperimentContainer";
import { markCostNeedsLoginB } from "@/utils/costNeedsResultScreen";
import {
  isQuestionContextTreatment,
  markQuestionContextLoginVariation,
  QUESTION_CONTEXT_COST_TEMPLATE_EXPERIMENT_KEY,
  QuestionContextVariation,
} from "@/utils/questionContextExperiment";
import { RequestQuestionStepByStep } from "./RequestQuestionUniqueStep/RequestQuestionStepByStep";
import MatchRequestOnboarding from "./MatchRequestOnboarding";

type OnboardingStep = "onboarding" | "form";

// HackleExperimentContainer effect의 의존성이라 모듈 스코프에서 참조를 고정한다.
const markCostTemplateTreatmentOnAssigned = (variation: ExperimentVariation) => {
  if (!isQuestionContextTreatment(variation)) return;
  markQuestionContextLoginVariation(variation);
  markCostNeedsLoginB();
};

/**
 * 실험 #312 B안 흐름.
 * A안의 첫문항("지금 가장 걱정되는 부분은?")을 온보딩 화면으로 대체하고,
 * 온보딩 CTA 클릭 시 지역 선택(RequestQuestionStepByStep step 1)부터 바로 시작한다.
 *
 * - Q1을 건너뛰므로 #310(첫문항 옵션 A/B) 실험과는 무관하다.
 * - type 미지정 → "price" 기본값(step 1 부가 문구 없음). 온보딩이 맥락을 이미 설명하므로 충분.
 * - 라우트 변경 없이 같은 페이지 내 로컬 스텝으로만 전환.
 */
export default function MatchRequestOnboardingFlow() {
  const [step, setStep] = useState<OnboardingStep>("onboarding");

  if (step === "onboarding") {
    return <MatchRequestOnboarding onNext={() => setStep("form")} />;
  }

  /*
   * B 에만 `needs="price"` 를 넘긴다.
   *
   * 이 prop 이 제출 시점에 `markMatchRequestNeeds` 를 심고, 로그인 화면이 그 마킹으로
   * 지역·조문객·비용 범위를 되짚는다(`CostNeedsRecap`). 이 경로는 첫문항을 안 거쳐
   * 원래 그 마킹이 없었고, 그래서 로그인이 `CostSavingsMessage` 로 흘렀다.
   *
   * A 는 넘기지 않는다. 대조군은 실험이 없을 때 보던 화면 그대로여야 한다.
   */
  const funnel = (variation: QuestionContextVariation) => (
    <RequestQuestionStepByStep
      showExpectedPriceRange={true}
      questionContextVariation={variation}
      needs={variation === "B" ? "price" : undefined}
    />
  );

  // CTA를 누른 뒤에만 마운트한다. 온보딩을 본 사람까지 노출로 세면 문항 카드의 효과를
  // 측정할 수 없다.
  return (
    <HackleExperimentContainer
      experimentKey={QUESTION_CONTEXT_COST_TEMPLATE_EXPERIMENT_KEY}
      onAssigned={markCostTemplateTreatmentOnAssigned}
      variants={{ A: funnel("A"), B: funnel("B") }}
      contextExtra={{ entry: "template_cost" }}
    />
  );
}
